// utils/offers.ts
import {config} from '@app/modules/config';
import {getNumberOfMonthsFromPeriod} from '@app/modules/offers/wording';
import type {
	AcqType,
	OffersAcquisitionResponse,
	OffersAcquisitionType,
	PlanType,
} from '@app/types/offersAcq';
import {getLegacyLocaleFromLocale} from '@deezer/deezer-compatibility';
import type {NextRouter} from 'next/router';

const PLAN_META: Record<
	PlanType,
	{id: number; color: string; trackKey: string}
> = {
	'annual-plan': {id: 1, color: '#a238ff', trackKey: 'annual-plan'},
	premium: {id: 1000001, color: '#a238ff', trackKey: 'premium'},
	duo: {id: 1000002, color: '#96f9f3', trackKey: 'duo'},
	family: {id: 1000006, color: '#ff673d', trackKey: 'family'},
	student: {id: 1005001, color: '#a238ff', trackKey: 'student'},
};

const BYPASS_CODE = ['202506_tiktok_acquisition'];

const now = () => new Date();

const hasActiveWindow = (o: OffersAcquisitionType, at = now()) => {
	const rawStart = (o as any)?.date_start?.date;
	const rawEnd = (o as any)?.date_end?.date;
	if (!rawStart || !rawEnd) return false;
	const start = new Date(rawStart);
	const end = new Date(rawEnd);
	return start <= at && end >= at;
};

const isSpecial = (o: OffersAcquisitionType) =>
	!!o.business_operation_code?.trim();

const matchesPlan = (plan: PlanType) => (o: OffersAcquisitionType) =>
	o.offer_id === PLAN_META[plan].id;

const notBypassed = (o: OffersAcquisitionType) =>
	!BYPASS_CODE.includes(o.business_operation_code ?? '');

/* ---------------------------------- */
/* Safe period accessors + classifiers*/
/* ---------------------------------- */

type Period =
	| {
			period: string;
			duration?: number;
			duration_type?: 'D' | 'M' | 'Y';
			iteration?: number;
			price?: string;
			price_total?: string;
			price_by_month?: string;
			reference?: string;
	  }
	| undefined;

const getPeriods = (o: OffersAcquisitionType) => {
	const coll = o.subscription_period_collection ?? ({} as any);
	const tryAndBuy: Period = coll?.try_and_buy;
	const initial: Period = coll?.initial;
	const renewal: Period = coll?.renewal;
	return {tryAndBuy, initial, renewal};
};

export const hasTry = (o: OffersAcquisitionType) => !!getPeriods(o).tryAndBuy;

const isDurationPromo = (o: OffersAcquisitionType) => {
	const {tryAndBuy, initial} = getPeriods(o);
	if (tryAndBuy) return false;
	return (initial?.duration ?? 1) > 1 && (initial?.iteration ?? 1) === 1;
};

const isIterationPromo = (o: OffersAcquisitionType) => {
	const {tryAndBuy, initial} = getPeriods(o);
	if (tryAndBuy) return false;
	return (initial?.iteration ?? 1) > 1;
};

// Only used in countdown logic (2–3 month "trial-like")
const isTrial2M3M = (o: OffersAcquisitionType) => {
	const d = getPeriods(o).tryAndBuy?.duration;
	return d === 2 || d === 3;
};

/* ---------------------------------- */
/* Pricing strategy helpers           */
/* ---------------------------------- */

const getDisplayMonthlyPrice = (o: OffersAcquisitionType) => {
	// For monthly plans, public display usually aligns with the steady state price
	// (renewal), even if there is a trial or intro promo.
	const {renewal, initial} = getPeriods(o);
	return (renewal?.price ?? initial?.price ?? '-') as string;
};

const getTrialInfo = (o: OffersAcquisitionType) => {
	const {tryAndBuy} = getPeriods(o);
	if (!tryAndBuy?.duration) return null;
	return {
		duration: tryAndBuy.duration,
		unit: tryAndBuy.duration_type ?? 'M',
	} as const;
};

const getIntroPromoInfo = (o: OffersAcquisitionType) => {
	const {initial, renewal} = getPeriods(o);
	return {
		duration: initial?.duration ?? 1,
		unit: (initial?.duration_type as 'D' | 'M' | 'Y') ?? 'M',
		iteration: initial?.iteration ?? 1,
		price: initial?.price ?? '-',
		renewalPrice: renewal?.price ?? '-',
	};
};

/* ---------------------------------- */
/* Promo months helper (fix)          */
/* ---------------------------------- */

const getPromoMonths = (o: OffersAcquisitionType): number | undefined => {
	const {tryAndBuy, initial} = getPeriods(o);

	if (tryAndBuy?.duration && tryAndBuy.duration_type === 'M') {
		return tryAndBuy.duration;
	}

	if (initial?.duration_type === 'M') {
		const dur = initial.duration ?? 1;
		const iter = initial.iteration ?? 1;

		if (iter > 1) return dur * iter;

		if (dur > 1) return dur;
	}

	return undefined;
};

/* ---------------------------------- */
/* Generic offer filter               */
/* ---------------------------------- */

const defaultOfferPredicate =
	(plan: PlanType) => (o: OffersAcquisitionType) => {
		if (!matchesPlan(plan)(o)) return false;
		if (!notBypassed(o)) return false;

		if (plan === 'annual-plan') return true;

		const publicOk = !!o.public;
		const specialOk = hasActiveWindow(o) && isSpecial(o);
		return publicOk || specialOk;
	};

/**
 * Single place to fetch the "primary" matching offer(s) for a plan.
 */
const selectOffers = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	predicate: (o: OffersAcquisitionType) => boolean = defaultOfferPredicate(
		plan,
	),
) => offers.filter(predicate);

/** Get first matching offer or undefined */
const firstOffer = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	predicate?: (o: OffersAcquisitionType) => boolean,
) => selectOffers(plan, offers, predicate)[0];

/* ---------------------- */
/* Small existing helpers */
/* ---------------------- */

function stripQueryAndHash(p: string) {
	return p.split('#')[0].split('?')[0];
}

export function resolvedPath(router: NextRouter) {
	let path = router.asPath ? stripQueryAndHash(router.asPath) : '';
	try {
		path = decodeURIComponent(path);
	} catch {
		/* noop */
	}

	if (path.includes('[')) {
		const {locale, query} = router;
		if (Array.isArray(query?.slug) && query.slug.length) {
			path = `/${locale || ''}/offers/${query.slug.join('/')}`.replace(
				/\/\//g,
				'/',
			);
		}
	}
	return path || '/';
}

/* --------------- */
/* Public API	  */
/* --------------- */

export const getPlanId = (plan: PlanType) => PLAN_META[plan]?.id ?? '';

export const getOfferColor = (plan: PlanType) =>
	PLAN_META[plan]?.color ?? '#a238ff';

/**
 * Prices to display for the plan cards (monthly steady-state).
 * Prefer renewal price (falls back to initial if needed).
 */
export const getOffersPrice = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) => selectOffers(plan, offers).map((o) => getDisplayMonthlyPrice(o));

export const getOfferMonthNumber = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) => selectOffers(plan, offers).map((o) => getPromoMonths(o) ?? 0);

const isCurrentofferHasTryAndBuy = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) => selectOffers(plan, offers).map((o) => hasTry(o));

export const currentofferHasTryAndBuy = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) => isCurrentofferHasTryAndBuy(plan, offers)[0] ?? false;

export const getOfferTrialDays = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) =>
	selectOffers(plan, offers).map((o) => {
		const {tryAndBuy} = getPeriods(o);
		if (tryAndBuy?.duration && tryAndBuy.duration_type === 'D') {
			return tryAndBuy.duration;
		}
		return undefined;
	});

export const getPlanTitle = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
) => {
	const planId = PLAN_META[plan].id;
	return offers
		.filter((o) =>
			plan !== 'annual-plan'
				? !!o.public && o.offer_id === planId
				: o.offer_id === planId,
		)
		.map((o) => o.label);
};

export const getOffersInfoByPlan = (
	plan: Exclude<PlanType, 'annual-plan'>,
	offers: OffersAcquisitionResponse,
) => {
	const o = firstOffer(plan, offers);
	if (!o) return undefined;
	const {initial, renewal, tryAndBuy} = getPeriods(o);

	return {
		initialPrice: initial?.price ?? renewal?.price ?? '-',
		offerId: o.offer_id,
		duration: tryAndBuy?.duration ?? undefined, // undefined si pas de trial
		label: o.label,
	};
};

export const getOfferPaymentUrlByPlan = (
	plan: PlanType,
	acqType: AcqType,
	offers: OffersAcquisitionResponse,
) => {
	const base = firstOffer(plan, offers)?.payment_journey_url;
	if (!base) return config.get('paymentHost');

	const acqSuffix = acqType === 'acq-2' ? 'acq-v2' : 'acq';
	const originParam = `${plan}-page-${acqSuffix}`;
	const sep = base.includes('?') ? '&' : '?';

	let geoipParam = '';
	if (typeof window !== 'undefined') {
		const urlParams = new URLSearchParams(window.location.search);
		const geoip = urlParams.get('GEOIP');
		if (geoip) {
			geoipParam = `&GEOIP=${encodeURIComponent(geoip)}`;
		}
	}

	return `${base}${sep}origin=${encodeURIComponent(originParam)}${geoipParam}`;
};

type OffersYearlyInfo = {
	initialPrice: string;
	price: string;
	price_by_month: string;
	months_duration: string;
	tnb_months_duration: string;
	label: string;
};

export const getOffersYearlyInfoByPlan = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
): OffersYearlyInfo | null => {
	const o = firstOffer(plan, offers, matchesPlan(plan)); // just match id
	if (!o) return null;

	const {initial, renewal, tryAndBuy} = getPeriods(o);
	return {
		initialPrice: initial?.price ?? '-',
		price: renewal?.price ?? '-',
		price_by_month: initial?.price_by_month ?? '-',
		months_duration: String(
			getNumberOfMonthsFromPeriod({...initial} as any) ?? '-',
		),
		tnb_months_duration: String(tryAndBuy?.duration ?? '-'),
		label: o.label ?? 'premium',
	};
};

export const getCountryFromLocale = (locale: string, locales: string[]) => {
	if (!locales.includes(locale)) {
		throw new Error(
			`i18n: the locale "${locale}" is invalid or not supported.`,
		);
	}
	return getLegacyLocaleFromLocale(locale);
};

export const fetchOffers = async ({
	locale,
	locales,
}: {
	locale: string;
	locales: string[];
}) => {
	const GEOIP = getCountryFromLocale(locale, locales);
	const response = await fetch(
		`${config.get(
			'deezerApiUrl',
		)}/ajax/gw-light.php?method=payment_getPlanCatalog&input=3&api_version=1.0&api_token=&cid=442313930&GEOIP=${GEOIP}`,
		{method: 'POST'},
	);
	const data = await response.json();
	return data;
};

/* ---------------------------------- */
/* T&C text builder                  */
/* ---------------------------------- */

export const getTncByPlan = (
	plan: PlanType,
	offers: OffersAcquisitionResponse,
	t: any,
): string[] => {
	if (plan === 'annual-plan') {
		const yearly = getOffersYearlyInfoByPlan(plan, offers);
		const monthsDur = +(yearly?.months_duration ?? 0);
		return [
			t('acqPages.text_ifcancelsubbeforeXmonthperiodnorefund', {
				sprintf: [yearly?.months_duration ?? '-'],
				count: monthsDur,
			}),
			t('acqPages.text_atendofXmonthperiodchargepriceXperyearunlessoptout', {
				sprintf: [yearly?.months_duration ?? '-', yearly?.price ?? '-'],
				count: monthsDur,
			}),
			t('acqPages.text_ifplanXsubscriberandupgradeimmediatechargeofpriceX', {
				sprintf: ['Deezer Premium', yearly?.initialPrice ?? '-'],
				count: monthsDur,
			}),
			t('acqPages.text_anycurrentmonthsubalreadypaidnotrefunded'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	const o = firstOffer(plan, offers);
	if (!o) {
		return [t('acqPages.text_nocombinationpossible')];
	}

	const label = getPlanTitle(plan, offers)[0] ?? 'Deezer Premium';
	const {initial, renewal} = getPeriods(o);
	const monthlyPrice = renewal?.price ?? initial?.price ?? '-';

	if (hasTry(o)) {
		const trial = getTrialInfo(o)!; // existe
		const trialValue =
			trial.unit === 'D'
				? t('acqPages.days', {
						count: trial.duration,
						defaultValue: `${trial.duration} days`,
					})
				: t('acqPages.months', {
						count: trial.duration,
						defaultValue: `${trial.duration} months`,
					});

		return [
			t('acqPages.text_offerXmonthtrialserviceplanX', {
				// clé legacy "month" mais on passe la valeur localisée (jours/mois)
				sprintf: [trialValue, label],
			}),
			t('acqPages.text_autoconvtoplanXpriceX', {
				sprintf: [label, monthlyPrice],
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	if (isDurationPromo(o)) {
		const info = getIntroPromoInfo(o);
		const introSpan =
			info.unit === 'M'
				? t('acqPages.months', {
						count: info.duration,
						defaultValue: `${info.duration} months`,
					})
				: info.unit === 'D'
					? t('acqPages.days', {
							count: info.duration,
							defaultValue: `${info.duration} days`,
						})
					: t('acqPages.years', {
							count: info.duration,
							defaultValue: `${info.duration} years`,
						});

		return [
			t('acqPages.text_intropriceForPeriodThenPricePerMonth', {
				sprintf: [info.price ?? '-', introSpan, monthlyPrice],
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	if (isIterationPromo(o)) {
		const info = getIntroPromoInfo(o);
		return [
			t('acqPages.text_intropricePerMonthForNMonthsThenPricePerMonth', {
				sprintf: [info.price ?? '-', info.iteration, monthlyPrice],
			}),
			t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
			t('acqPages.text_newusersonly'),
			t('acqPages.text_onetimeonlyperuser'),
			t('acqPages.text_nocombinationpossible'),
		];
	}

	return [
		t('acqPages.text_standardMonthlyNoCommitment', {
			sprintf: [label, monthlyPrice],
			defaultValue: `${label} for ${monthlyPrice} / month — no commitment`,
		}),
		t('acqPages.text_offernonbindingmaycancelsubscriptionatanytime'),
		t('acqPages.text_nocombinationpossible'),
	];
};

/* Tracking page-name from path -> single map instead of switch */

const TRACK_MAP: Record<string, string> = {
	'/offers/premium/acq/': 'premium-acq',
	'/offers/premium/acq-2/': 'premium-acq-v2',
	'/offers/family/acq/': 'family-acq',
	'/offers/family/acq-2/': 'family-acq-v2',
	'/offers/annual-plan/acq/': 'annual-plan-acq',
	'/offers/duo/acq/': 'duo-acq',
	'/offers/student/acq/': 'student-acq',
};

export const getTrackAcqPageName = (path: string) => TRACK_MAP[path] ?? '';

/* ---------------------------------- */
/* Countdown rule in one place        */
/* ---------------------------------- */

export const getExpiringPromoDeadlineMs = (
	plan: PlanType,
	acqType: AcqType,
	offers: OffersAcquisitionResponse | undefined,
): number | null => {
	if (!offers?.length) return null;

	const nowDt = now();
	const inSixDaysMs = nowDt.getTime() + 6 * 24 * 60 * 60 * 1000;

	const eligible =
		(plan === 'premium' && (acqType === 'acq' || acqType === 'acq-2')) ||
		(plan === 'student' && acqType === 'acq') ||
		plan === 'duo' ||
		plan === 'family';

	if (!eligible) return null;

	const offersForPlan = selectOffers(plan, offers, (o) => {
		if (!matchesPlan(plan)(o) || !notBypassed(o)) return false;
		if (!hasActiveWindow(o, nowDt)) return false;

		if (plan === 'premium' || plan === 'student') {
			if (!isSpecial(o)) return false;
		} else {
			// accept either official specials or 2–3M "trial-like"
			if (!isSpecial(o) && !isTrial2M3M(o)) return false;
		}

		const rawEnd = (o as any)?.date_end?.date;
		const endMs = rawEnd ? new Date(rawEnd).getTime() : NaN;
		if (!isFinite(endMs)) return false;

		return endMs <= inSixDaysMs;
	});

	const deadlineMs = offersForPlan
		.map((o) => {
			const rawEnd = (o as any)?.date_end?.date;
			return rawEnd ? new Date(rawEnd).getTime() : Infinity;
		})
		.filter((ms) => isFinite(ms))
		.sort((a, b) => a - b)[0];

	return isFinite(deadlineMs) ? deadlineMs : null;
};

export const getExpiringPromoDeadline = (
	plan: PlanType,
	acqType: AcqType,
	offers: OffersAcquisitionResponse | undefined,
): Date | null => {
	const ms = getExpiringPromoDeadlineMs(plan, acqType, offers);
	return ms ? new Date(ms) : null;
};
